import { useMemo, type ReactElement, type ReactNode } from 'react';

import type { PatientViewModel } from '@/models/patient';

import { CareTeamPanel } from './panels/CareTeamPanel';
import { FamilyPanel } from './panels/FamilyPanel';
// Exploratory: a side-rail counterpart to the demographics-tab
// IdentifiersCard. Implemented as a try, not committed to as product.
// Kept on disk in case we revisit; not mounted in the rail today.
// import { IdentifiersPanel } from './panels/IdentifiersPanel';
import { SortableSidePanels } from './panels/SortableSidePanels';
import type { SortablePanelItem } from './panels/types';
import { TasksPanel } from './panels/TasksPanel';

interface PatientSidePanelsProps {
  readonly patient: PatientViewModel;
}

/**
 * Right rail of the patient record. Composes the contextual panels —
 * care team, family memberships, tasks — and lets the user reorder them
 * via drag-and-drop. The order persists per-user in localStorage.
 */
export function PatientSidePanels({
  patient,
}: PatientSidePanelsProps): ReactElement {
  const items = useMemo<readonly SortablePanelItem[]>(
    () => [
      // Exploratory IdentifiersPanel entry — left commented out so the
      // try-out is easy to re-enable without re-deriving the wiring.
      // Not committed to as product yet.
      // {
      //   id: 'identifiers',
      //   render: (handle: ReactNode): ReactElement => (
      //     <IdentifiersPanel
      //       guid={patient.guid}
      //       identifiers={patient.identifiers}
      //       dragHandle={handle}
      //     />
      //   ),
      // },
      {
        id: 'careTeam',
        render: (handle: ReactNode): ReactElement => (
          <CareTeamPanel patient={patient} dragHandle={handle} />
        ),
      },
      {
        id: 'family',
        render: (handle: ReactNode): ReactElement => (
          <FamilyPanel patient={patient} dragHandle={handle} />
        ),
      },
      {
        id: 'tasks',
        render: (handle: ReactNode): ReactElement => (
          <TasksPanel guid={patient.guid} dragHandle={handle} />
        ),
      },
    ],
    [patient],
  );

  return (
    <aside className="flex flex-col gap-3">
      <SortableSidePanels items={items} />
    </aside>
  );
}
